import { XCircleIcon, XMarkIcon } from "@heroicons/react/24/outline";
import Dialog from "../dialogs";
import { useEffect, useState } from "react";
import SelectBox from "../commons/SelectBox";
import CustomSlider from "../commons/CustomSlider";
import InputWithIcon from "../commons/InputWithIcon";
import { Cell, Pie, PieChart, ResponsiveContainer } from "recharts";
import ToggleSwitch from "../commons/ToggleSwitch";
import TokenPercentageSelector from "../commons/TokenPercentageSelector";
import {
  formatCurrency,
  formatMinutes,
  getAssetCoin,
  getAssetName,
} from "../../utils";
import { baseAxios } from "../../apis";
import { useCsrfToken } from "../../hooks/useCsrfToken";
import Drawer from "../commons/Drawer";
import { IconArrowDoubleSide, IconLoading } from "../../assets/icons";
import { ChevronLeftIcon } from "@heroicons/react/16/solid";
import DialogSuccess from "../dialogs/DialogSuccess";
import { useBot } from "../../context/botContext";
import CoinLogo from "../commons/CoinLogo";
import BotLogo from "../commons/BotLogo";
import { useBotDemo } from "../../context/botDemoContext";
// import Checkbox from "../commons/Checkbox";
// import { useTranslat/ion } from "react-i18next";
// import { ChevronDownIcon } from "@heroicons/react/24/solid";

const DrawerActivateBot3Demo = ({
  open,
  onClose,
  onConfirm,
  selectedBot,
  onBack,
  form,
  isEdit,
}) => {
  const { fetchBotListsDemo, fetchBotDetailDemo } = useBotDemo();
  const [openDialog, setOpenDialog] = useState(null);
  // const { t } = useTranslation();
  const [activeIndex, setActiveIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const { fetchCsrfToken } = useCsrfToken();
  const [errorText, setErrorText] = useState("");


  const handleSubmit = async () => {
    let formValue;
    setErrorText("");

    try {
      setLoading(true);
      formValue = {
        ...form,
      };
      delete formValue?.exchange;
      if (selectedBot?.bot_name?.toLowerCase() === "rebalance") {
        formValue = {
          ...form,
          settings: {
            ...form?.settings,
            take_profit_value: form?.settings?.take_profit_percentage,
          },
        };
        delete formValue?.settings?.take_profit_percentage;
      }

      // 🔒 Fetch CSRF token
      const token = await fetchCsrfToken();

      // 🔐 Post with token
      if (isEdit) {
        await baseAxios.patch(
          `/api/demo/bot_detail/${selectedBot.active_bot_id}/edit-settings/`,
          { settings: formValue.settings },
          {
            headers: {
              "X-CSRFToken": token,
            },
          },
        );
      } else {
        await baseAxios.post("/api/demo/bot_create/", formValue, {
          headers: {
            "X-CSRFToken": token,
          },
        });
      }

      fetchBotListsDemo(true);

      if (isEdit) {
        fetchBotDetailDemo(selectedBot.active_bot_id);
      }
      setOpenDialog("success");
    } catch (err) {
      const apiError = err?.response?.data;

      setErrorText(
        apiError?.error
          ? apiError.error
          : apiError?.non_field_errors?.length
            ? apiError.non_field_errors.join(", ")
            : "Something went wrong. Please try again later.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (loading) return;
    setErrorText("");
    onClose();
  };

  return (
    <Drawer open={open} onClose={handleClose} width="600px" className="">
      {/* dialogsuccess */}
      <DialogSuccess
        open={openDialog === "success"}
        onClose={() => {
          setOpenDialog(null);
          onClose();
        }}
        title={
          isEdit
            ? "Bot update successful – your bot settings have been saved!"
            : "Bot activation complete – your new bot is now running!"
        }
      />
      {/* end dialogsuccess */}
      <div className="h-full flex flex-col justify-between ">
        {/* <div className="flex items-center justify-end w-full border-b border-[#D9D9D9] pb-1"> */}
        <div>
          <div className="flex items-center justify-between w-full relative">
            <button
              className="bg-transparent hover:bg-transparent text-[#007A78] flex !p-0"
              onClick={() => {
                if (loading) return;
                setErrorText("");
                onBack();
              }}
            >
              <ChevronLeftIcon className="w-[20px] h-[20px]" /> Back
            </button>
            <h2 className="text-xl font-bold ">Confirmation</h2>
            <button
              className="bg-transparent hover:bg-transparent text-black !p-0"
              onClick={() => {
                if (loading) return;
                setErrorText("");
                onClose();
              }}
            >
              <XMarkIcon className="w-[18px] h-[18px]" />
            </button>
          </div>
          <div className="mt-5 space-y-5">
            <h2 className="text-base font-bold text-[#697077]">
              Bot Settings Preview
            </h2>
            {selectedBot?.bot_name?.toLowerCase() === "rebalance" ? (
              <RebalanceComponent form={form} selectedBot={selectedBot} />
            ) : selectedBot?.bot_name?.toLowerCase() === "dca" ? (
              <DCAComponent form={form} selectedBot={selectedBot} />
            ) : (
              <XenoarcComponent form={form} selectedBot={selectedBot} />
            )}
          </div>
        </div>

        <div className="mt-8">
          {errorText && (
            <span className="block text-sm mb-2 text-[#ff0000]">
              {errorText}
            </span>
          )}
          <button
            type="submit"
            disabled={loading}
            className="w-full relative overflow-hidden !py-3 !font-bold !text-lg !bg-[#007A78]"
            onClick={handleSubmit}
          >
            {loading ? (
              <div className="animate-spin w-fit flex justify-center items-center mx-auto">
                <IconLoading className="w-[24px] h-[24px]" />
              </div>
            ) : isEdit ? (
              "Update The Bot"
            ) : (
              "Activate Now"
            )}
          </button>
        </div>
      </div>
    </Drawer>
  );
};

export default DrawerActivateBot3Demo;

const DCAComponent = ({ form, selectedBot }) => {
  return (
    <>
      <div className="bg-[#F2F4F8] p-5 space-y-4 rounded-xl border border-[#DDE1E6]">
        <div className="flex items-center gap-3">
          <div className="flex items-center -space-x-3">
            <img
              src={`/images/icons/icon_wallet_${form?.exchange?.name.toLowerCase()}.svg`}
              alt=""
              className="w-full max-w-[40px] h-[40px] rounded-full bg-white border"
            />
            <CoinLogo
              asset={getAssetName(form.exchange?.pair)}
              className="w-full max-w-[40px] h-[40px] rounded-full bg-white border"
            />
          </div>
          <IconArrowDoubleSide className="w-[30px] h-[30px] text-[#B3B3B3]" />
          <BotLogo botName={selectedBot?.bot_name} />
        </div>
        <div className="flex text-sm items-center justify-between">
          <span className="text-[#697077]">Exchange</span>
          <span className="uppercase">{form?.exchange?.name}</span>
        </div>
        <div className="flex text-sm items-center justify-between">
          <span className="text-[#697077]">Pair</span>
          <span>{form?.exchange?.pair}</span>
        </div>
      </div>
      <div className="p-5 space-y-4 rounded-xl border border-[#DDE1E6]">
        <div className="flex text-sm items-center justify-between">
          <strong className="text-[#697077]">Capital</strong>
          <span>
            {formatCurrency(form?.settings?.capital || 0)}{" "}
            {form?.exchange?.pair?.split("/")[1] || "USDT"}
          </span>
        </div>
        {/* <div className="flex text-sm items-center justify-between">
          <strong className="text-[#697077]">DCA Focus</strong>
          <span>Selling</span>
        </div> */}
        <div className="flex text-sm items-center justify-between">
          <strong className="text-[#697077]">Trade Size</strong>
          <span>
            {formatCurrency(form?.settings?.amount || 0)}{" "}
            {form?.exchange?.pair?.split("/")[1] || "USDT"}
          </span>
        </div>
        <div className="flex text-sm items-center justify-between">
          <strong className="text-[#697077]">Maximum order</strong>
          <span>
            {formatCurrency(form?.settings?.max_order_count || 0)} order
          </span>
        </div>
        <div className="flex text-sm items-center justify-between">
          <strong className="text-[#697077]">Price changes</strong>
          <span>
            {formatCurrency(form?.settings?.price_change_percentage || 0)} %
          </span>
        </div>
        <div className="flex text-sm items-center justify-between">
          <strong className="text-[#697077]">Take profit (buy back)</strong>
          <span>
            {formatCurrency(form?.settings?.take_profit_percentage || 0)} %
          </span>
        </div>
        <div className="flex text-sm items-center justify-between">
          <strong className="text-[#697077]">Trigger price</strong>
          <span>
            {formatCurrency(form?.settings?.trigger_price || 0)}{" "}
            {form?.exchange?.pair?.split("/")[1] || "USDT"}
          </span>
        </div>
        {/* <div className="flex text-sm items-center justify-between">
          <strong className="text-[#697077]">Stop loss</strong>
          <span>9.00%</span>
        </div> */}
      </div>
    </>
  );
};

const RebalanceComponent = ({ form, selectedBot }) => {
  return (
    <>
      <div className="bg-[#F2F4F8] p-5 space-y-4 rounded-xl border border-[#DDE1E6]">
        <div className="flex items-center gap-3">
          <div className="flex items-center -space-x-3">
            <img
              src={`/images/icons/icon_wallet_${form.exchange?.name}.svg`}
              alt={form.exchange?.name}
              className="w-full max-w-[40px] h-[40px] rounded-full"
            />
            {/* <img
              src={getAssetCoin(form.exchange?.pair.split("/")[0])}
              alt={form.exchange?.pair.split("/")[0]}
              className="w-full max-w-[40px] h-[40px] rounded-full"
            /> */}
            <CoinLogo
              asset={getAssetName(form.exchange?.pair)}
              className="w-full max-w-[40px] h-[40px] rounded-full"
            />
          </div>
          <IconArrowDoubleSide className="w-[30px] h-[30px] text-[#B3B3B3]" />
          <img
            src={
              selectedBot?.bot_name?.toLowerCase() === "rebalance"
                ? "/images/icons/icon_bot_rebalance.svg"
                : selectedBot?.bot_name?.toLowerCase() === "dca"
                  ? "/images/icons/icon_bot_dca.svg"
                  : "/images/icons/icon_bot_xenoarc.svg"
            }
            alt=""
            className="w-full max-w-[40px] rounded-full"
          />
        </div>
        <div className="flex text-sm items-center justify-between">
          <span className="text-[#697077]">Exchange</span>
          <span className="uppercase">{form?.exchange?.name}</span>
        </div>
        {/* <div className="flex text-sm items-center justify-between">
          <span className="text-[#697077]">Pair</span>
          <span>BTC/USDT</span>
        </div> */}
      </div>
      <div className="bg-[#F2F4F8] p-5 space-y-4 rounded-xl border border-[#DDE1E6]">
        <div className="flex text-sm items-center justify-between">
          <span className="text-[#697077]">Capital</span>
          <span className="uppercase">
            {formatCurrency(form.settings.capital || 0)}{" "}
            {form?.exchange?.pair?.split("/")[1] || "USDT"}
          </span>
        </div>
        <hr />
        <div className="flex flex-col text-sm">
          <span className="font-bold text-[#697077]">Allocation</span>
          {form.settings.allocations.map((allocation, idx) => (
            <div
              key={allocation.coin_symbol + idx}
              className="mt-1 pl-3 flex text-sm items-center justify-between"
            >
              <span>{allocation.coin_symbol}</span>
              <span>{allocation.allocation_percentage}%</span>
            </div>
          ))}
        </div>
        <div className="flex text-sm items-center justify-between">
          <strong className="text-[#697077]">Rebalance method</strong>
          <span>
            {form.settings.pattern === 0 ? "Proportionally" : "Periodically"}
          </span>
        </div>
        <div className="flex text-sm items-center justify-between">
          <strong className="text-[#697077]">Rebalance setting</strong>
          <span>
            {form.settings.pattern === 0
              ? `${form.settings.divergence_rate}%`
              : `Every ${formatMinutes(
                  form.settings.time_designation || 0,
                  false,
                )}`}
          </span>
        </div>
        <div className="flex text-sm items-center justify-between">
          <strong className="text-[#697077]">Take Profit</strong>
          <span>
            {formatCurrency(form?.settings?.take_profit_value || 0)}{" "}
            {form?.exchange?.pair?.split("/")[1] || "USDT"} (
            {form?.settings?.take_profit_percentage || 0}%)
          </span>
        </div>
      </div>
    </>
  );
};

const XenoarcComponent = ({ form, selectedBot }) => {
  return (
    <>
      <div className="bg-[#F2F4F8] p-5 space-y-4 rounded-xl border border-[#DDE1E6]">
        <div className="flex items-center gap-3">
          <div className="flex items-center -space-x-3">
            <img
              src={`/images/icons/icon_wallet_${form?.exchange?.name.toLowerCase()}.svg`}
              alt=""
              className="w-full max-w-[40px] h-[40px] rounded-full bg-white border"
            />
            <CoinLogo
              asset={getAssetName(form.exchange?.pair)}
              className="w-full max-w-[40px] h-[40px] rounded-full bg-white border"
            />
          </div>
          <IconArrowDoubleSide className="w-[30px] h-[30px] text-[#B3B3B3]" />
          <BotLogo botName={selectedBot?.bot_name} />

          {/* <img
            src={
              selectedBot?.bot_name?.toLowerCase() === "rebalance"
                ? "/images/icons/icon_bot_rebalance.svg"
                : selectedBot?.bot_name?.toLowerCase() === "dca"
                ? "/images/icons/icon_bot_dca.svg"
                : "/images/icons/icon_bot_xenoarc.svg"
            }
            alt=""
            className="w-full max-w-[40px] rounded-full"
          /> */}
        </div>
        <div className="flex text-sm items-center justify-between">
          <span className="text-[#697077]">Exchange</span>
          <span className="uppercase">{form?.exchange?.name}</span>
        </div>
        <div className="flex text-sm items-center justify-between">
          <span className="text-[#697077]">Pair</span>
          <span>{form?.exchange?.pair}</span>
        </div>
      </div>

      {/* v2 */}

      {selectedBot?.bot_name?.toLowerCase() === "xeno arc" && (
        <div className="p-5 space-y-4 rounded-xl border border-[#DDE1E6]">
          <div className="flex text-sm items-center justify-between">
            <span className="text-[#697077]">Capital</span>
            <span>
              {formatCurrency(form?.settings?.capital || 0)}{" "}
              {form?.exchange?.pair?.split("/")[1] || "USDT"}
            </span>
          </div>
          <div className="flex text-sm items-center justify-between">
            <span className="text-[#697077]">Trade Size</span>
            <span>
              {formatCurrency(form?.settings?.amount || 0)}{" "}
              {form?.exchange?.pair?.split("/")[1] || "USDT"}
            </span>
          </div>
          <div className="flex text-sm items-center justify-between">
            <span className="text-[#697077]">Number of step</span>
            <span>{form?.settings?.number_of_grids} levels</span>
          </div>
          <div className="flex text-sm items-center justify-between">
            <span className="text-[#697077]">Buy-step width</span>
            <span>{form?.settings?.grid_width}%</span>
          </div>
          <div className="flex text-sm items-center justify-between">
            <span className="text-[#697077]">Sell-step width</span>
            <span>{form?.settings?.grid_width_exit}%</span>
          </div>
          <div className="flex text-sm items-center justify-between">
            <span className="text-[#697077]">Buy-flag return</span>
            <span>{form?.settings?.buy_flg_return}%</span>
          </div>
          <div className="flex text-sm items-center justify-between">
            <span className="text-[#697077]">Exit-flag return</span>
            <span>{form?.settings?.exit_flg_return}%</span>
          </div>
        </div>
      )}
      {selectedBot?.bot_name?.toLowerCase() === "grid" && (
        <div className="p-5 space-y-4 rounded-xl border border-[#DDE1E6]">
          <div className="flex text-sm items-center justify-between">
            <span className="text-[#697077]">Capital</span>
            <span>
              {formatCurrency(form?.settings?.amount || 0)}{" "}
              {form?.exchange?.pair?.split("/")[1] || "USDT"}
            </span>
          </div>
          <div className="flex text-sm items-center justify-between">
            <span className="text-[#697077]">Trade Size</span>
            <span>
              {formatCurrency(form?.settings?.trade_size || 0)}{" "}
              {form?.exchange?.pair?.split("/")[1] || "USDT"}
            </span>
          </div>
          <div className="flex text-sm items-center justify-between">
            <span className="text-[#697077]">Number of step</span>
            <span>{form?.settings?.number_of_grids} levels</span>
          </div>
          <div className="flex text-sm items-center justify-between">
            <span className="text-[#697077]">Buy-step width</span>
            <span>{form?.settings?.grid_width}%</span>
          </div>
          {/* <div className="flex text-sm items-center justify-between">
            <span className="text-[#697077]">Buy after bounce</span>
            <span>{form?.settings?.buy_after_bounce}%</span>
          </div> */}
          <div className="flex text-sm items-center justify-between">
            <span className="text-[#697077]">Sell-step width</span>
            <span>{form?.settings?.grid_width_exit}%</span>
          </div>
          {/* <div className="flex text-sm items-center justify-between">
            <span className="text-[#697077]">Sell after bounce</span>
            <span>{form?.settings?.sell_after_bounce}%</span>
          </div> */}
        </div>
      )}
    </>
  );
};
